import { SESSION_STORAGE_KEY, readStoredSession } from "@/lib/auth-storage";

let accessTokenExpiresAt: number | null = null;

type StoredSessionWithExpiry = {
  accessTokenExpiresAt?: number;
};

/**
 * `oktogo_session` belongs to the customer app. The management SPA runs on the
 * same origin but has its own session, so it must neither read its expiry from
 * that blob (it would inherit the other app's clock, skip a needed refresh and
 * get bounced to login) nor write into it. Management keeps expiry in memory
 * only; its token is short-lived and a reload re-derives it from the JWT.
 */
let scope: "web" | "management" = "web";

export function setTokenExpiryScope(next: "web" | "management"): void {
  scope = next;
}

/** Persist `accessTokenExpiresAt = now + expiresIn` (seconds from server). */
export function recordTokenExpiry(expiresIn: number): void {
  if (!Number.isFinite(expiresIn) || expiresIn <= 0) return;
  accessTokenExpiresAt = Date.now() + expiresIn * 1000;
  if (scope === "web") persistExpiresAtToSessionBlob(accessTokenExpiresAt);
}

/** Read in-memory expiry, then (web only) the optional field on `oktogo_session`. */
export function getAccessTokenExpiresAt(): number | null {
  if (accessTokenExpiresAt != null) return accessTokenExpiresAt;
  if (scope !== "web") return null;
  const stored = readStoredSession<StoredSessionWithExpiry>();
  if (typeof stored?.accessTokenExpiresAt === "number" && stored.accessTokenExpiresAt > 0) {
    accessTokenExpiresAt = stored.accessTokenExpiresAt;
    return accessTokenExpiresAt;
  }
  return null;
}

export function clearTokenExpiry(): void {
  accessTokenExpiresAt = null;
}

/** Seconds until access token expiry; `null` when unknown. */
export function getRemainingAccessSec(): number | null {
  const at = getAccessTokenExpiresAt();
  if (at == null) return null;
  return Math.max(0, Math.floor((at - Date.now()) / 1000));
}

/** Bootstrap / restore when API did not return `expiresIn` — decode JWT `exp`. */
export function recordTokenExpiryFromAccessToken(accessToken: string): void {
  const remainingSec = decodeAccessTokenRemainingSec(accessToken);
  if (remainingSec != null && remainingSec > 0) {
    recordTokenExpiry(remainingSec);
  }
}

function decodeAccessTokenRemainingSec(token: string): number | null {
  try {
    const segment = token.split(".")[1];
    if (!segment) return null;
    const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(base64)) as { exp?: number };
    if (typeof payload.exp === "number") {
      return Math.max(0, Math.floor(payload.exp - Date.now() / 1000));
    }
  } catch {
    /* malformed JWT */
  }
  return null;
}

function persistExpiresAtToSessionBlob(expiresAt: number): void {
  const storage = localStorage.getItem(SESSION_STORAGE_KEY) ? localStorage : sessionStorage;
  const raw = storage.getItem(SESSION_STORAGE_KEY);
  if (!raw) return;
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    storage.setItem(
      SESSION_STORAGE_KEY,
      JSON.stringify({ ...parsed, accessTokenExpiresAt: expiresAt }),
    );
  } catch {
    /* ignore malformed blob */
  }
}
